import SEO from "@/components/SEO";
import Icon from "@/components/common/Icon";
import { PageHero, CTAButton, SectionLabel, MoveCare } from "@/components/common/Bits";
import { Reveal, WindowGrid } from "@/components/motion/Reveal";
import { COMPANY, waLink, WA_MESSAGES } from "@/data/company";
import { WHO_WE_HELP } from "@/data/mockData";

const values = [
    { title: "Care", icon: "HeartHandshake" },
    { title: "Reliability", icon: "ShieldCheck" },
    { title: "Professionalism", icon: "BadgeCheck" },
    { title: "Convenience", icon: "Sparkles" },
    { title: "Accountability", icon: "ClipboardCheck" },
    { title: "Quality Workmanship", icon: "Hammer" },
];

export default function About() {
    return (
        <>
            <SEO
                title="About Us — One Team for Every Move"
                path="/about-us"
                description="ProCare.SG supports homeowners, tenants, landlords, agents and businesses with coordinated one-stop property care across Singapore."
            />
            <PageHero
                label="About ProCare.SG"
                title="One Team for Every MOVE"
                subtitle="ProCare.SG supports property owners, tenants, landlords, agents and businesses with dependable, coordinated property services across Singapore."
                image="/images/about-team.webp"
            />

            {/* Intro + brand story */}
            <section className="py-24">
                <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-2">
                    <Reveal>
                        <div className="relative overflow-hidden rounded-[28px] rounded-tr-[80px] shadow-brand-lg">
                            <img src="/images/about-team.webp" alt="The ProCare.SG team in Singapore" className="aspect-[4/5] w-full object-cover" loading="lazy" />
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <SectionLabel>Our story</SectionLabel>
                        <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight text-brand-blue sm:text-4xl">
                            <MoveCare text="Moving is more than transport. It’s a list of CARE." />
                        </h2>
                        <div className="mt-6 space-y-4 text-base leading-relaxed text-brand-ink/70">
                            <p>Every move creates a list: cleaning, repairs, painting and improvements. Coordinating separate contractors for each is stressful and slow.</p>
                            <p>ProCare.SG was created to bring these services together. We take care of the property so you can focus on your move — with one team and one point of contact.</p>
                            <p>Whether it's an HDB flat, a condominium, a landed home or a commercial space, we help make every move smoother.</p>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Mission / Vision */}
            <section className="bg-brand-surface py-24">
                <div className="mx-auto grid max-w-7xl gap-6 px-6 md:grid-cols-2">
                    <Reveal>
                        <div className="h-full rounded-3xl bg-white p-9 shadow-brand">
                            <SectionLabel>Mission</SectionLabel>
                            <p className="mt-5 font-display text-2xl font-medium leading-snug text-brand-blue">
                                To make property moves easier by providing dependable, coordinated and complete property care solutions.
                            </p>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className="relative h-full overflow-hidden rounded-3xl bg-brand-blue p-9 text-white grain">
                            <div className="pointer-events-none absolute -right-8 -top-8 opacity-10"><WindowGrid size={160} /></div>
                            <SectionLabel light>Vision</SectionLabel>
                            <p className="mt-5 font-display text-2xl font-medium leading-snug">
                                To become a trusted one-stop property care partner for homes and businesses across Singapore.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Values */}
            <section className="py-24">
                <div className="mx-auto max-w-7xl px-6">
                    <Reveal className="max-w-2xl">
                        <SectionLabel>What we value</SectionLabel>
                        <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight text-brand-blue sm:text-4xl">The values behind our care</h2>
                    </Reveal>
                    <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {values.map((v, i) => (
                            <Reveal key={v.title} delay={i * 0.05}>
                                <div className="flex items-center gap-4 rounded-3xl border border-brand-blue/10 bg-white p-6">
                                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-red/10 text-brand-red">
                                        <Icon name={v.icon} className="h-6 w-6" />
                                    </div>
                                    <span className="font-display text-lg font-semibold text-brand-blue">{v.title}</span>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Who we serve */}
            <section className="bg-brand-surface py-24">
                <div className="mx-auto max-w-7xl px-6">
                    <Reveal className="max-w-2xl">
                        <SectionLabel>Who we serve</SectionLabel>
                        <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight text-brand-blue sm:text-4xl">Built for everyone in the property journey</h2>
                    </Reveal>
                    <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {WHO_WE_HELP.map((w, i) => (
                            <Reveal key={w.title} delay={i * 0.05}>
                                <div className="h-full rounded-3xl bg-white p-7 shadow-brand">
                                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-blue text-white">
                                        <Icon name={w.icon} className="h-6 w-6" />
                                    </div>
                                    <h3 className="mt-5 font-display text-xl font-semibold text-brand-blue">{w.title}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-brand-ink/60">{w.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Company details */}
            <section className="py-24">
                <div className="mx-auto max-w-4xl px-6">
                    <Reveal className="rounded-[28px] border border-brand-blue/10 bg-white p-9 shadow-brand">
                        <h3 className="font-display text-2xl font-semibold text-brand-blue">Company Details</h3>
                        <dl className="mt-6 grid gap-x-8 gap-y-4 sm:grid-cols-2">
                            <div><dt className="text-xs uppercase tracking-widest text-brand-red">Company</dt><dd className="mt-1 text-brand-ink">{COMPANY.name}</dd></div>
                            <div><dt className="text-xs uppercase tracking-widest text-brand-red">UEN</dt><dd className="mt-1 text-brand-ink">{COMPANY.uen}</dd></div>
                            <div><dt className="text-xs uppercase tracking-widest text-brand-red">Email</dt><dd className="mt-1 text-brand-ink">{COMPANY.email}</dd></div>
                            <div><dt className="text-xs uppercase tracking-widest text-brand-red">Mobile</dt><dd className="mt-1 text-brand-ink">{COMPANY.mobile}</dd></div>
                            <div className="sm:col-span-2"><dt className="text-xs uppercase tracking-widest text-brand-red">Address</dt><dd className="mt-1 text-brand-ink">{COMPANY.address.line1}, {COMPANY.address.line2}, {COMPANY.address.line3}</dd></div>
                        </dl>
                        <div className="mt-8">
                            <CTAButton href={waLink(WA_MESSAGES.general)} whatsapp testid="about-whatsapp-cta">Chat With Us on WhatsApp</CTAButton>
                        </div>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
